import { useCallback, useEffect, useState } from "react";
import { answerDeskService, FaqEntry } from "../../services/answerDeskService";
import { DeliveryArea, deliveryAreaService } from "../../services/deliveryAreaService";
import { apiErrorMessage } from "./answerDeskUtils";

/** Everything the Answer Desk pages need: the visible entries (searched in
 * the browser) and the active delivery areas ({delivery_charges}). */
export function useAnswerDeskData(includeArchived = false) {
    const [entries, setEntries] = useState<FaqEntry[]>([]);
    const [deliveryAreas, setDeliveryAreas] = useState<DeliveryArea[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const reload = useCallback(async () => {
        setError(null);
        try {
            setEntries(await answerDeskService.listEntries(includeArchived));
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't load the answers. Check your connection and refresh."));
        } finally {
            setLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        let cancelled = false;
        deliveryAreaService
            .getDeliveryAreas(0, 500, true)
            .then((res) => {
                if (!cancelled) setDeliveryAreas(res.items ?? []);
            })
            .catch(() => {
                /* {delivery_charges} just stays unfilled */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const upsert = useCallback((entry: FaqEntry) => {
        setEntries((prev) =>
            prev.some((e) => e.faq_entry_id === entry.faq_entry_id)
                ? prev.map((e) => (e.faq_entry_id === entry.faq_entry_id ? entry : e))
                : [entry, ...prev],
        );
    }, []);

    const removeLocal = useCallback((id: string) => {
        setEntries((prev) => prev.filter((e) => e.faq_entry_id !== id));
    }, []);

    const bumpCopyCount = useCallback((id: string) => {
        setEntries((prev) =>
            prev.map((e) => (e.faq_entry_id === id ? { ...e, copy_count: e.copy_count + 1 } : e)),
        );
    }, []);

    return { entries, deliveryAreas, loading, error, setError, reload, upsert, removeLocal, bumpCopyCount };
}
